<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  props: {
    show: Boolean,
    idWorkOrder: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      BACKEND_URL_VERSION: process.env.VUE_APP_BACKEND_URL_VERSION,
      isDownloading: false,
      isLoading: false,
      form: {
        file: null,
      },
      isFinished: false,
      result: {
        totalSuccess: 0,
        totalFailed: 0,
        success: [],
        failed: [],
      },
    };
  },

  watch: {
    modalShow(val) {
      if (val) this.resetForm();
    },
  },

  computed: {
    modalShow: {
      get() {
        return this.show;
      },
      set(val) {
        if (!val) this.$emit("close");
      },
    },
  },

  methods: {
    resetForm() {
      this.resetFile();
      this.isFinished = false;
      this.result = {
        totalSuccess: 0,
        totalFailed: 0,
        success: [],
        failed: [],
      };
    },

    resetFile() {
      this.form.file = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
    },

    handleFileChange(event) {
      this.form.file = event.target.files[0] ?? null;
    },

    downloadTemplate() {
      if (this.isDownloading) return;
      this.isDownloading = true;

      axios({
        method: "get",
        // TODO: sesuaikan url dengan endpoint download template yang disediakan BE
        url: this.BACKEND_URL_VERSION + "work-order/detail/download-template",
        params: { id_work_order: this.idWorkOrder },
        responseType: "blob",
        headers: {
          Authorization: "Bearer " + localStorage.access_token,
        },
      })
        .then((response) => {
          const blobUrl = window.URL.createObjectURL(
            new Blob([response.data], {
              type:
                response.headers["content-type"] ||
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            })
          );

          let fileName = "template_peserta.xlsx";
          const contentDisposition = response.headers["content-disposition"];
          if (contentDisposition) {
            const match = contentDisposition.match(
              /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
            );
            if (match && match[1]) fileName = decodeURIComponent(match[1]);
          }

          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);

          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Template berhasil diunduh",
            timer: 1500,
            timerProgressBar: true,
            showConfirmButton: false,
          });
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Gagal",
            text:
              err.response?.data?.meta?.message || "Gagal mengunduh template.",
          });
        })
        .finally(() => {
          this.isDownloading = false;
        });
    },

    store() {
      if (this.isLoading) return;

      if (!this.form.file) {
        Swal.fire({
          icon: "warning",
          title: "Peringatan",
          text: "File template tidak boleh kosong.",
        });
        return;
      }

      const formData = new FormData();
      formData.append("file", this.form.file);
      formData.append("id_work_order", this.idWorkOrder);

      this.isLoading = true;
      this.isFinished = false;

      axios({
        method: "post",
        url: this.BACKEND_URL_VERSION + "work-order/detail/import",
        data: formData,
        headers: {
          Authorization: "Bearer " + localStorage.access_token,
          "Content-Type": "multipart/form-data",
        },
      })
        .then((response) => {
          const responseData = response.data?.data ?? {};

          this.result = {
            totalSuccess: responseData.total_success ?? 0,
            totalFailed: responseData.total_failed ?? 0,
            success: responseData.success ?? [],
            failed: responseData.failed ?? [],
          };

          const allSuccess = this.result.totalFailed === 0;
          this.isFinished = !allSuccess;

          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text:
              response.data?.meta?.message ||
              `Import selesai. ${this.result.totalSuccess} berhasil, ${this.result.totalFailed} gagal.`,
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false,
          });

          if (this.result.totalSuccess > 0) this.$emit("finish");

          this.resetFile();

          if (allSuccess) {
            this.$emit("close");
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Gagal",
            text:
              err.response?.data?.data ||
              err.response?.data?.meta?.message ||
              "Gagal mengimport data.",
          });

          this.resetFile();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <b-modal v-model="modalShow" title="Import Data Peserta" size="md" hide-footer>
    <b-form-group class="fw-medium">
      <template #label>
        <div class="d-flex justify-content-between align-items-center">
          <span>File Template</span>
          <b-button
            variant="success"
            size="sm"
            :disabled="isDownloading"
            @click="downloadTemplate"
          >
            <span v-if="isDownloading">
              <i class="fa fa-spinner fa-spin me-1"></i> Mengunduh...
            </span>
            <span v-else>
              <i class="fa fa-download me-1"></i> Download Template
            </span>
          </b-button>
        </div>
      </template>
      <input
        type="file"
        ref="fileInput"
        class="form-control"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        @change="handleFileChange"
      />
    </b-form-group>

    <div class="alert alert-success mt-3" role="alert" v-if="isFinished">
      <h4 class="alert-heading">Import Selesai</h4>
      <table class="table table-sm mb-0">
        <tr>
          <td>Total Berhasil</td>
          <td>:</td>
          <td>{{ result.totalSuccess }}</td>
        </tr>
        <tr>
          <td>Total Gagal</td>
          <td>:</td>
          <td>{{ result.totalFailed }}</td>
        </tr>
      </table>

      <div class="table-responsive mt-2" v-if="result.failed.length > 0">
        <table class="table table-sm table-bordered bg-white mb-0">
          <thead>
            <tr>
              <th>Row</th>
              <th>NIP</th>
              <th>Alasan Gagal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in result.failed" :key="index">
              <td>{{ item.row }}</td>
              <td>{{ item.nip }}</td>
              <td>{{ item.reason }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="text-end mt-3">
      <b-button variant="secondary" class="me-2" @click="$emit('close')">
        Close
      </b-button>
      <b-button variant="primary" :disabled="isLoading" @click="store">
        <span v-if="isLoading">
          <i class="fa fa-spinner fa-spin me-1"></i> Menyimpan...
        </span>
        <span v-else>Simpan</span>
      </b-button>
    </div>
  </b-modal>
</template>
